/**
 * sidebarLevel — which "level" the sidenav shows, derived from the URL.
 *
 * The sidenav is nested rather than flat: each level replaces the previous
 * one (with a slide transition) instead of expanding inline.
 *
 *   home       workspace landing — bot list (recency-sorted) + workspace tools
 *   bot        a bot is in scope   — back-to-home, bot switcher, bot pages
 *   call-logs  /b/{id}/call-logs   — back-to-bot, switcher, View + Filters
 *   config     /b/{id}/config      — back-to-bot, switcher, settings submenu
 *              (also /b/{id}/schema-bot, opened from What it says)
 *   operate    /b/{id}/operate     — back-to-bot, switcher, Operations submenu
 *   workflow   …/config?tab=workflow — the bot's conversation workflows, one
 *              nest deeper than Settings (it replaced an in-canvas panel)
 *
 * `LEVEL_DEPTH` drives the transition direction: going to a deeper level
 * slides forward (new content in from the right), going shallower slides back.
 */

export type SidebarLevel = 'home' | 'admin' | 'bot' | 'call-logs' | 'config' | 'operate' | 'workflow';

export interface SidebarScope {
  level: SidebarLevel;
  /** Bot the URL is scoped to, when the path is under `/b/{botId}`. */
  botId?: string;
}

export const LEVEL_DEPTH: Record<SidebarLevel, number> = {
  home: 0,
  admin: 1,
  bot: 1,
  'call-logs': 2,
  config: 2,
  operate: 2,
  workflow: 3,
};

/** The `?tab=` value whose settings page gets its own, deeper sidebar level. */
const WORKFLOW_TAB = 'workflow';

/** `?tab=` for a config URL's search string (`''` when absent). */
export function configTabFromSearch(search: string): string {
  const qs = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(qs).get('tab') || '';
}

/** Split a `pathname?search#hash` string (what `useUrl()` returns). */
export function splitLocation(url: string): { pathname: string; search: string } {
  const hashAt = url.indexOf('#');
  const noHash = hashAt >= 0 ? url.slice(0, hashAt) : url;
  const qAt = noHash.indexOf('?');
  return {
    pathname: qAt >= 0 ? noHash.slice(0, qAt) : noHash,
    search: qAt >= 0 ? noHash.slice(qAt) : '',
  };
}

/**
 * Map a location onto a sidebar level. Anything outside `/b/{botId}` is a
 * workspace surface (`/customer`, `/customer/billing`, `/help`, `/admin`, …)
 * and shows the home level.
 *
 * `search` is only consulted for `/config`, where `?tab=workflow` drills one
 * level deeper than the rest of Settings — the conversation-workflow editor
 * gets the bot's workflow list in the sidebar. Callers that pass no search
 * still land on `config`.
 */
export function sidebarScopeForPath(pathname: string, search = ''): SidebarScope {
  // The admin console (`/admin`, `/admin/billing`) gets its own level.
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return { level: 'admin' };
  const m = pathname.match(/^\/b\/([^/]+)(?:\/([^/]+))?/);
  if (!m) return { level: 'home' };
  const botId = decodeURIComponent(m[1]);
  const section = m[2] || '';
  if (section === 'call-logs') return { level: 'call-logs', botId };
  if (section === 'config') {
    return { level: configTabFromSearch(search) === WORKFLOW_TAB ? 'workflow' : 'config', botId };
  }
  if (section === 'operate') return { level: 'operate', botId };
  // The schema playground is reached from Settings → What it says; keep that
  // submenu open (pageIdForLocation lights the row) instead of popping up.
  if (section === 'schema-bot') return { level: 'config', botId };
  return { level: 'bot', botId };
}

/**
 * URL for `newBotId` that keeps the section the user is currently in — so
 * switching bots from `/b/A/call-logs?outcome=resolved` lands on
 * `/b/B/call-logs?outcome=resolved`, from `/b/A/config?tab=voices` on
 * `/b/B/config?tab=voices`, etc. Only the first path segment is preserved
 * (bot-specific sub-ids like a call id are dropped); the query string is kept.
 */
export function botSectionHref(pathname: string, search: string, newBotId: string): string {
  const m = pathname.match(/^\/b\/[^/]+(?:\/([^/?#]+))?/);
  const section = m && m[1] ? m[1] : '';
  return `/b/${newBotId}${section ? `/${section}` : ''}${search || ''}`;
}

/** Config `?tab=` → the settings-leaf node id it highlights in the sidebar. */
const CONFIG_TAB_PAGE_ID: Record<string, string> = {
  prompts: 'bot:prompts',
  workflow: 'bot:workflow',
  voices: 'bot:voice',
  glossary: 'bot:pronunciation',
  languages: 'bot:languages',
  telephony: 'bot:telephony',
  calls: 'bot:calls',
  escalation: 'bot:escalation',
  session: 'bot:session',
  tuner: 'bot:tuner',
  passive: 'bot:passive',
  chat: 'bot:chat',
  templates: 'bot:templates',
  general: 'bot:brand',
  governance: 'bot:compliance',
  tools: 'bot:tools',
  integrations: 'bot:integrations',
  advanced: 'bot:advanced',
};

/**
 * The nav node id the current URL corresponds to, used to highlight the active
 * row in the sidebar. Mirrors the `to()` builders in `tree.ts` in reverse.
 * Falls back to the workspace overview for anything unrecognized.
 */
export function pageIdForLocation(pathname: string, search = '', hash = ''): string {
  const bot = pathname.match(/^\/b\/([^/]+)(?:\/(.*))?$/);
  if (bot) {
    const rest = (bot[2] || '').replace(/\/$/, '');
    if (rest === '' ) return 'bot:dial';
    if (rest === 'manage' || rest === 'hero') return 'bot:overview';
    // Operations is a drillable group (see tree.ts): the ids below are its
    // LEAVES, so the trail highlights both the child row in the operate level
    // and the parent Operations row in the bot level.
    if (rest === 'operate/do-not-call') return 'bot:dnc';
    if (rest === 'operate' || rest.startsWith('operate/sequences')) return 'bot:operate-campaigns';
    if (rest.startsWith('call-logs')) return 'bot:call-logs';
    if (rest === 'help') return 'help';
    if (rest === 'api-docs') return 'api-docs';
    if (rest === 'conversation') return 'bot:workflow';
    // The schema playground is reached from "What it says" — keep that row lit.
    if (rest === 'schema-bot') return 'bot:prompts';
    if (rest === 'config') {
      const tab = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('tab') || 'prompts';
      return CONFIG_TAB_PAGE_ID[tab] || 'bot:prompts';
    }
    return 'bot:overview';
  }
  if (pathname === '/help') return 'help';
  if (pathname === '/api-docs') return 'api-docs';
  if (pathname.startsWith('/customer')) {
    if (/\/billing|\/usage/.test(pathname)) return 'billing';
    if (/\/packages/.test(pathname)) return 'packages';
    if (/\/tasks/.test(pathname)) return 'task-board';
    if (/\/workflows/.test(pathname)) return 'workflows';
    if (hash === '#team') return 'team';
    return 'customer-overview';
  }
  if (pathname.startsWith('/reseller')) return 'reseller';
  if (pathname.startsWith('/admin/billing')) return 'platform-billing';
  return 'customer-overview';
}
